// Unified draft save state schema.
//
// A single host-local save blob per draft instance. Every phase of
// the draft. Gathering, pick-and-pass, deckbuilding, round
// matches, finals, rewards. Is recorded in this ONE structure.
// No sub-instances, no parallel save files.
//
// The blob is written by the host via instanceSaveAdapter (same
// path as a regular match save) and is tagged with the instance id
// so it surfaces as a single row in SessionBrowser. When the host
// resumes, this full object is handed to the draft runtime as its
// source of truth.
//
// Spec:
//   docs/superpowers/specs/2026-04-24-unified-instance-save-system-design.md

// --- Phase enum ---
//
// The draft progresses linearly through these phases. Every entry
// in `phaseHistory` stamps the transition so crash-recovery UIs
// can reconstruct the timeline. `phase` is always the latest.
export const DRAFT_PHASES = Object.freeze({
  GATHERING: 'gathering',         // waiting for pod to fill
  PICKING: 'picking',             // pack open + pass around
  DECKBUILDING: 'deckbuilding',   // each player builds from picks
  ROUND: 'round',                 // round matches in progress (+ roundNumber)
  FINALS: 'finals',               // optional top-cut bracket
  COMPLETED: 'completed',         // rewards distributed, archived soon
});

// --- Match status (per pairing) ---
export const MATCH_STATUS = Object.freeze({
  PENDING: 'pending',      // not yet started
  ACTIVE: 'active',        // currently playing
  COMPLETED: 'completed',  // winner recorded
  BYE: 'bye',              // odd pod. One participant auto-advances
  FORFEIT: 'forfeit',      // one side abandoned / 72h grace
});

// --- Factory ---
//
// Called once when a draft instance is first created OR when
// someone opens a fresh save after migration. `config` mirrors the
// fields the instance setup dialog collects: pod size, format,
// etc.
export function createInitialDraftState({
  instanceId,
  visibility,
  podSize = 4,
  format = null,
  hostUserId,
}) {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    instanceId,
    visibility,
    mode: 'draft',
    podSize,
    format,
    hostUserId,

    // Current lifecycle marker + transition log.
    phase: DRAFT_PHASES.GATHERING,
    roundNumber: 0,
    phaseHistory: [{ phase: DRAFT_PHASES.GATHERING, at: now }],

    // Pod roster. `deckId` is set during deckbuilding, not at join
    // time (drafts build decks from picks, not from pre-built
    // collections). `draftedCards` accumulates during picking.
    participants: [],

    // Pack-and-pass state. `packQueue[playerId]` is the stack of
    // packs currently in front of that player. Populated when
    // picking begins, consumed pick-by-pick, emptied when picking
    // phase completes.
    packQueue: {},

    // Pointer to the pick currently in flight (used for resume).
    // Shape: { round, direction: 'left' | 'right', picksMade }.
    currentPick: null,

    // Round scheduling. Each round contains pairings; each pairing
    // tracks its own match lifecycle and winner.
    //   rounds: [
    //     { roundNumber, startedAt, pairings: [
    //       { player1Id, player2Id, status, winnerId?, matchRoomCode? }
    //     ] }
    //   ]
    rounds: [],

    // Final standings array (set on transition to COMPLETED).
    // Shape: [{ userId, wins, losses, place }]
    standings: null,

    // Reward card distribution per-place. Shape:
    //   { place, userId, cardId, foiling }
    rewards: [],

    createdAt: now,
    updatedAt: now,
  };
}

// --- Pure state transitions ---
//
// Each returns a NEW state object. Callers persist via
// instanceSaveAdapter after applying.

export function advancePhase(state, nextPhase, { roundNumber } = {}) {
  if (!Object.values(DRAFT_PHASES).includes(nextPhase)) {
    throw new Error(`Unknown draft phase: ${nextPhase}`);
  }
  const at = new Date().toISOString();
  return {
    ...state,
    phase: nextPhase,
    roundNumber: roundNumber ?? state.roundNumber,
    phaseHistory: [...state.phaseHistory, { phase: nextPhase, at }],
    updatedAt: at,
  };
}

export function addParticipant(state, { userId, name }) {
  if (state.participants.some((p) => p.userId === userId)) return state;
  return {
    ...state,
    participants: [
      ...state.participants,
      { userId, name, deckId: null, draftedCards: [] },
    ],
    updatedAt: new Date().toISOString(),
  };
}

export function removeParticipant(state, { userId }) {
  return {
    ...state,
    participants: state.participants.filter((p) => p.userId !== userId),
    updatedAt: new Date().toISOString(),
  };
}

export function setParticipantDeck(state, { userId, deckId }) {
  return {
    ...state,
    participants: state.participants.map((p) =>
      p.userId === userId ? { ...p, deckId } : p,
    ),
    updatedAt: new Date().toISOString(),
  };
}

export function recordPick(state, { userId, cardId, packRemaining }) {
  const participants = state.participants.map((p) =>
    p.userId === userId
      ? { ...p, draftedCards: [...p.draftedCards, cardId] }
      : p,
  );
  const packQueue = { ...state.packQueue };
  if (packRemaining !== undefined) {
    packQueue[userId] = packRemaining;
  }
  return {
    ...state,
    participants,
    packQueue,
    updatedAt: new Date().toISOString(),
  };
}

export function setRoundPairings(state, { roundNumber, pairings }) {
  const normalizedPairings = pairings.map((p) => ({
    player1Id: p.player1Id,
    player2Id: p.player2Id,
    status: p.player2Id ? MATCH_STATUS.PENDING : MATCH_STATUS.BYE,
    winnerId: p.player2Id ? null : (p.player1Id || null),
    matchRoomCode: null,
    startedAt: null,
    completedAt: null,
  }));
  const existing = state.rounds.filter((r) => r.roundNumber !== roundNumber);
  const next = [...existing, {
    roundNumber,
    startedAt: new Date().toISOString(),
    pairings: normalizedPairings,
  }].sort((a, b) => a.roundNumber - b.roundNumber);
  return { ...state, rounds: next, updatedAt: new Date().toISOString() };
}

export function startPairing(state, { roundNumber, pairingIdx, matchRoomCode }) {
  return mapPairing(state, roundNumber, pairingIdx, (p) => ({
    ...p,
    status: MATCH_STATUS.ACTIVE,
    matchRoomCode,
    startedAt: new Date().toISOString(),
  }));
}

export function recordPairingResult(state, { roundNumber, pairingIdx, winnerId, forfeit = false }) {
  return mapPairing(state, roundNumber, pairingIdx, (p) => ({
    ...p,
    status: forfeit ? MATCH_STATUS.FORFEIT : MATCH_STATUS.COMPLETED,
    winnerId,
    completedAt: new Date().toISOString(),
  }));
}

function mapPairing(state, roundNumber, pairingIdx, fn) {
  const rounds = state.rounds.map((r) => {
    if (r.roundNumber !== roundNumber) return r;
    return {
      ...r,
      pairings: r.pairings.map((p, i) => (i === pairingIdx ? fn(p) : p)),
    };
  });
  return { ...state, rounds, updatedAt: new Date().toISOString() };
}

// --- Derived helpers (no mutation) ---

export function isRoundComplete(state, roundNumber) {
  const round = state.rounds.find((r) => r.roundNumber === roundNumber);
  if (!round) return false;
  return round.pairings.every((p) =>
    p.status === MATCH_STATUS.COMPLETED
    || p.status === MATCH_STATUS.BYE
    || p.status === MATCH_STATUS.FORFEIT);
}

export function computeStandings(state) {
  const tally = new Map();
  for (const p of state.participants) {
    tally.set(p.userId, { userId: p.userId, wins: 0, losses: 0, place: null });
  }
  for (const round of state.rounds) {
    for (const pairing of round.pairings) {
      const isFinal = pairing.status === MATCH_STATUS.COMPLETED
        || pairing.status === MATCH_STATUS.FORFEIT
        || pairing.status === MATCH_STATUS.BYE;
      if (!isFinal) continue;
      if (!pairing.winnerId) continue;
      const winner = tally.get(pairing.winnerId);
      if (winner) winner.wins += 1;
      const loserId = pairing.winnerId === pairing.player1Id
        ? pairing.player2Id
        : pairing.player1Id;
      if (loserId) {
        const loser = tally.get(loserId);
        if (loser) loser.losses += 1;
      }
    }
  }
  // Rank by wins desc, losses asc. Ties share a place.
  const rows = [...tally.values()].sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    return a.losses - b.losses;
  });
  let lastKey = '';
  let lastPlace = 0;
  rows.forEach((row, i) => {
    const key = `${row.wins}:${row.losses}`;
    if (key !== lastKey) {
      lastPlace = i + 1;
      lastKey = key;
    }
    row.place = lastPlace;
  });
  return rows;
}

export function setStandings(state, standings) {
  return { ...state, standings, updatedAt: new Date().toISOString() };
}

export function setRewards(state, rewards) {
  return { ...state, rewards, updatedAt: new Date().toISOString() };
}
